// client/src/components/Notifications/NotificationToast.js
// ─── Real-Time Toast Notifications ─────────────────────────────────────────
// Global component mounted in App.js. Listens to NotificationContext for new
// arrivals, then renders a stack of up to 3 glassmorphic toast cards with
// priority-based styling, auto-dismiss timers, and swipe-to-dismiss on mobile.
// ────────────────────────────────────────────────────────────────────────────

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha, keyframes } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Slide,
  Portal,
} from '@mui/material';

// ─── MUI Icons (same mapping as NotificationCenter) ──────────────────────
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TimerIcon from '@mui/icons-material/Timer';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PaymentsIcon from '@mui/icons-material/Payments';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SchoolIcon from '@mui/icons-material/School';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { useNotification } from '../../contexts/NotificationContext';

// ─── Constants ────────────────────────────────────────────────────────────

const MAX_VISIBLE_TOASTS = 3;
const DISMISS_NORMAL_MS = 5000;
const DISMISS_HIGH_MS = 8000;
const SLIDE_DURATION_MS = 300;
const SWIPE_THRESHOLD = 80; // px needed to dismiss via swipe

/** Map notification type → icon component */
const TYPE_ICON_MAP = {
  budget_alert: WarningAmberIcon,
  receipt_nudge: ReceiptLongIcon,
  trial_warning: TimerIcon,
  insight: LightbulbIcon,
  settlement_reminder: PaymentsIcon,
  spending_anomaly: TrendingUpIcon,
  security: ShieldIcon,
  weekly_report: AssessmentIcon,
  onboarding: SchoolIcon,
};

/** Map notification type → accent colour */
const TYPE_COLOR_MAP = {
  budget_alert: '#f59e0b',
  receipt_nudge: '#8b5cf6',
  trial_warning: '#ef4444',
  insight: '#10b981',
  settlement_reminder: '#3b82f6',
  spending_anomaly: '#f97316',
  security: '#ef4444',
  weekly_report: '#06b6d4',
  onboarding: '#6366f1',
};

/** Priority → left-border + tint colour */
const PRIORITY_STYLES = {
  urgent: { border: '#ef4444', tint: '#ef4444' },
  high: { border: '#f59e0b', tint: '#f59e0b' },
  normal: { border: null, tint: null },
  low: { border: null, tint: null },
};

// ─── Animations ───────────────────────────────────────────────────────────

const progressShrink = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

// ─── Toast Card Sub-component ─────────────────────────────────────────────

const ToastCard = React.memo(function ToastCard({
  toast,
  onDismiss,
  onView,
  isMobile,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();

  // ── Touch / swipe state ──
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const cardRef = useRef(null);

  const { notification, visible } = toast;
  const priority = notification.priority || 'normal';
  const type = notification.type || 'general';
  const priorityStyle = PRIORITY_STYLES[priority] || PRIORITY_STYLES.normal;
  const typeColor = TYPE_COLOR_MAP[type] || theme.palette.primary.main;
  const IconComponent = TYPE_ICON_MAP[type] || NotificationsIcon;

  const isLow = priority === 'low';
  const isUrgentOrHigh = priority === 'urgent' || priority === 'high';
  const autoDismissMs =
    isUrgentOrHigh ? DISMISS_HIGH_MS : DISMISS_NORMAL_MS;

  // ── Swipe handlers (mobile) ──
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e) => {
    const delta = e.touches[0].clientX - touchStartX.current;
    touchDeltaX.current = delta;
    if (cardRef.current) {
      // Only allow swiping to the right (positive delta)
      const translate = Math.max(0, delta);
      cardRef.current.style.transform = `translateX(${translate}px)`;
      cardRef.current.style.opacity = String(
        Math.max(0.3, 1 - translate / (SWIPE_THRESHOLD * 2))
      );
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchDeltaX.current > SWIPE_THRESHOLD) {
      // Swipe threshold met → dismiss
      if (cardRef.current) {
        cardRef.current.style.transition = 'transform 200ms ease, opacity 200ms ease';
        cardRef.current.style.transform = 'translateX(400px)';
        cardRef.current.style.opacity = '0';
      }
      setTimeout(() => onDismiss(toast.id), 200);
    } else {
      // Snap back
      if (cardRef.current) {
        cardRef.current.style.transition = 'transform 200ms ease, opacity 200ms ease';
        cardRef.current.style.transform = 'translateX(0)';
        cardRef.current.style.opacity = '1';
        // Clear transition after snap-back so future drags feel instant
        setTimeout(() => {
          if (cardRef.current) {
            cardRef.current.style.transition = '';
          }
        }, 200);
      }
    }
  }, [onDismiss, toast.id]);

  // ── Truncated message ──
  const truncatedMessage = useMemo(() => {
    const msg = notification.message || '';
    return msg.length > 100 ? msg.slice(0, 97) + '…' : msg;
  }, [notification.message]);

  // ── Glassmorphism & priority backgrounds ──
  const cardBg = useMemo(() => {
    const base = isDark
      ? 'rgba(30, 41, 59, 0.85)'
      : 'rgba(255, 255, 255, 0.88)';
    if (priorityStyle.tint) {
      return isDark
        ? alpha(priorityStyle.tint, 0.12)
        : alpha(priorityStyle.tint, 0.06);
    }
    return base;
  }, [isDark, priorityStyle.tint]);

  return (
    <Slide
      direction={isMobile ? 'down' : 'left'}
      in={visible}
      timeout={SLIDE_DURATION_MS}
      mountOnEnter
      unmountOnExit
    >
      <Box
        ref={cardRef}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: isLow ? 1.25 : 1.5,
          mb: 1,
          width: isMobile ? '100%' : 380,
          maxWidth: '100vw',
          borderRadius: '12px',
          borderLeft: priorityStyle.border
            ? `4px solid ${priorityStyle.border}`
            : `4px solid ${typeColor}`,
          background: cardBg,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: isDark
            ? `0 8px 32px ${alpha('#000', 0.4)}`
            : `0 8px 32px ${alpha('#000', 0.12)}`,
          border: `1px solid ${
            isDark
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.06)'
          }`,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          // Ensure it sits above most things
          willChange: 'transform, opacity',
        }}
      >
        {/* ── Left: Type Icon ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isLow ? 32 : 40,
            height: isLow ? 32 : 40,
            minWidth: isLow ? 32 : 40,
            borderRadius: '10px',
            background: alpha(typeColor, isDark ? 0.2 : 0.12),
          }}
        >
          <IconComponent
            sx={{
              fontSize: isLow ? 18 : 22,
              color: typeColor,
            }}
          />
        </Box>

        {/* ── Centre: Title + Message ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={isLow ? 'body2' : 'subtitle2'}
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {notification.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              color: theme.palette.text.secondary,
              lineHeight: 1.35,
              opacity: isLow ? 0.7 : 0.85,
            }}
          >
            {truncatedMessage}
          </Typography>
        </Box>

        {/* ── Right: View + Close ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            ml: 0.5,
            flexShrink: 0,
          }}
        >
          {notification.actionUrl && (
            <Button
              size="small"
              onClick={() => onView(toast)}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'none',
                color: typeColor,
                '&:hover': {
                  background: alpha(typeColor, 0.1),
                },
              }}
              endIcon={
                <OpenInNewIcon sx={{ fontSize: '14px !important' }} />
              }
            >
              {t('notifications.view', 'View')}
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => onDismiss(toast.id)}
            sx={{
              color: theme.palette.text.secondary,
              p: 0.5,
              '&:hover': {
                color: theme.palette.text.primary,
                background: alpha(theme.palette.text.primary, 0.08),
              },
            }}
            aria-label={t('notifications.dismiss', 'Dismiss')}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* ── Auto-dismiss progress bar ── */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: alpha(
              priorityStyle.tint || typeColor,
              isDark ? 0.3 : 0.2
            ),
            transformOrigin: 'left',
            animation: `${progressShrink} ${autoDismissMs}ms linear forwards`,
          }}
        />
      </Box>
    </Slide>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────

export default function NotificationToast() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { notifications } = useNotification();

  // ── Internal toast queue ──
  const [toasts, setToasts] = useState([]);
  const prevLengthRef = useRef(0);
  const timersRef = useRef(new Map()); // id → timeoutId

  // ── Detect new notifications by tracking array length ──
  useEffect(() => {
    const prevLen = prevLengthRef.current;
    const currLen = notifications.length;

    if (currLen > prevLen && prevLen > 0) {
      // New items were prepended — grab the newly added ones
      const newCount = currLen - prevLen;
      const newNotifications = notifications.slice(0, newCount);

      setToasts((prev) => {
        const incoming = newNotifications.map((n) => ({
          id: n._id || n.id || `toast-${Date.now()}-${Math.random()}`,
          notification: n,
          visible: true,
          timestamp: Date.now(),
        }));

        // Prepend new toasts; enforce max visible limit
        const merged = [...incoming, ...prev];
        if (merged.length > MAX_VISIBLE_TOASTS) {
          // Dismiss the oldest beyond the limit
          const overflow = merged.slice(MAX_VISIBLE_TOASTS);
          overflow.forEach((t) => clearToastTimer(t.id));
          return merged.slice(0, MAX_VISIBLE_TOASTS);
        }
        return merged;
      });
    }

    prevLengthRef.current = currLen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length]);

  // ── Set up auto-dismiss timers whenever toasts change ──
  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.visible && !timersRef.current.has(toast.id)) {
        const priority = toast.notification.priority || 'normal';
        const isUrgentOrHigh = priority === 'urgent' || priority === 'high';
        const delay = isUrgentOrHigh ? DISMISS_HIGH_MS : DISMISS_NORMAL_MS;

        const timerId = setTimeout(() => {
          dismissToast(toast.id);
        }, delay);
        timersRef.current.set(toast.id, timerId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts]);

  // ── Cleanup all timers on unmount ──
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  // ── Clear a single timer ──
  const clearToastTimer = useCallback((id) => {
    if (timersRef.current.has(id)) {
      clearTimeout(timersRef.current.get(id));
      timersRef.current.delete(id);
    }
  }, []);

  // ── Dismiss a toast (animate out, then remove) ──
  const dismissToast = useCallback(
    (id) => {
      clearToastTimer(id);
      // First hide (triggers Slide exit animation)
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, visible: false } : t))
      );
      // Then remove from DOM after animation completes
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, SLIDE_DURATION_MS + 50);
    },
    [clearToastTimer]
  );

  // ── View action → navigate + dismiss ──
  const handleView = useCallback(
    (toast) => {
      const url = toast.notification.actionUrl;
      if (url) {
        // If it's an internal path, use navigate; otherwise open in new tab
        if (url.startsWith('/')) {
          navigate(url);
        } else {
          window.open(url, '_blank', 'noopener');
        }
      }
      dismissToast(toast.id);
    },
    [navigate, dismissToast]
  );

  // ── Nothing to render ──
  if (toasts.length === 0) return null;

  return (
    <Portal>
      <Box
        role="region"
        aria-live="polite"
        aria-label="Notifications"
        sx={{
          position: 'fixed',
          zIndex: theme.zIndex.snackbar + 10,
          // Desktop: top-right; Mobile: top-center
          ...(isMobile
            ? {
                top: 'env(safe-area-inset-top, 12px)',
                left: 12,
                right: 12,
              }
            : {
                top: 16,
                right: 16,
              }),
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          pointerEvents: 'none',
          // Children need pointer events
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {toasts.map((toast) => (
          <ToastCard
            key={toast.id}
            toast={toast}
            onDismiss={dismissToast}
            onView={handleView}
            isMobile={isMobile}
          />
        ))}
      </Box>
    </Portal>
  );
}
